import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TRIAGE_REASON_LABELS } from '../../../models/LiveMeetingsStore';
import {
  ExpandedPanel,
  PanelGrid,
  PanelField,
  PanelLabel,
  PanelValue,
  PanelSection,
  PanelSectionTitle,
  ParticipantList,
  ParticipantChip,
  PanelActions,
  PrimaryButton,
  SecondaryButton,
  FormRow,
  FormField,
  FormLabel,
  FormSelect,
  FormTextarea,
  FormErrorText,
  StatusBadge,
} from '../LiveMeetingsDashboard.styles';

function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function TriagePanel({ item, liveStore, adminStore, mode }) {
  // mode: 'assign' | 'review' | null (view only)
  const { triageData, meetingData, participantEmails } = item;
  const triageId = triageData?.triageId;

  const [companyId, setCompanyId]   = useState('');
  const [userId, setUserId]         = useState('');
  const [users, setUsers]           = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [notes, setNotes]           = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState(null);

  const companies = adminStore?.companies || [];

  // Build a map of companyId → name for resolving identified users' company affiliations
  const companyNameById = Object.fromEntries(
    companies.map(c => [c.company.id, c.company.name])
  );

  // Identify Multiply users by email for quick lookup
  const identifiedUsers = triageData?.identifiedUsers || [];
  const multiplyUserByEmail = Object.fromEntries(
    identifiedUsers.map(u => [u.email, u])
  );

  // Use enriched participants (with names) if available, fall back to plain emails
  const enrichedParticipants = (triageData?.participants || []).filter(p => p.email);
  const participantList = enrichedParticipants.length > 0
    ? enrichedParticipants
    : participantEmails.map(email => ({ email, name: null }));

  // Load users when company selection changes
  useEffect(() => {
    if (!companyId) { setUsers([]); setUserId(''); return; }
    setLoadingUsers(true);
    setUserId('');
    axios.get(`/companies/${companyId}/users`)
      .then(res => setUsers(Array.isArray(res.data) ? res.data : []))
      .catch(() => setUsers([]))
      .finally(() => setLoadingUsers(false));
  }, [companyId]);

  const handleAssign = async () => {
    if (!companyId) { setError('Please select a company.'); return; }
    if (!userId) { setError('Please select a user to assign this meeting to.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      await liveStore.assignTriage(triageId, { companyId, assignedTo: userId, notes: notes || null });
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Assignment failed.');
      setSubmitting(false);
    }
  };

  const handleReview = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await liveStore.markTriageReviewed(triageId, notes || null);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Action failed.');
      setSubmitting(false);
    }
  };

  const isProcessed = triageData?.status && triageData.status !== 'pending';

  const triageReason = TRIAGE_REASON_LABELS[triageData?.triageReason] || triageData?.triageReason || '—';
  const priority     = triageData?.priority;

  return (
    <ExpandedPanel>
      {/* Summary row */}
      <PanelGrid $cols={3}>
        <PanelField>
          <PanelLabel>Triage Reason</PanelLabel>
          <PanelValue>{triageReason}</PanelValue>
        </PanelField>

        <PanelField>
          <PanelLabel>Priority</PanelLabel>
          <PanelValue>
            {priority === 'high'
              ? <StatusBadge $variant="high">High</StatusBadge>
              : <StatusBadge>Normal</StatusBadge>}
          </PanelValue>
        </PanelField>

        <PanelField>
          <PanelLabel>Created</PanelLabel>
          <PanelValue>{formatDateTime(triageData?.createdAt || item.createdAt)}</PanelValue>
        </PanelField>

        <PanelField>
          <PanelLabel>Triage ID</PanelLabel>
          <PanelValue style={{ fontFamily: 'monospace', fontSize: 12 }}>{triageId || '—'}</PanelValue>
        </PanelField>

        <PanelField>
          <PanelLabel>Bot ID</PanelLabel>
          <PanelValue style={{ fontFamily: 'monospace', fontSize: 12 }}>{triageData?.botId || meetingData?.id || '—'}</PanelValue>
        </PanelField>
      </PanelGrid>

      {/* Participants */}
      {participantList.length > 0 && (
        <PanelSection>
          <PanelSectionTitle>Attendees ({participantList.length})</PanelSectionTitle>
          <ParticipantList>
            {participantList.map(p => {
              const multiplyUser = multiplyUserByEmail[p.email];
              const isMultiply = !!multiplyUser;
              // Only resolve company names for confirmed Multiply users
              const companyNames = isMultiply
                ? (multiplyUser.companyIds || []).map(id => companyNameById[id]).filter(Boolean)
                : [];
              return (
                <ParticipantChip key={p.email} $multiply={isMultiply}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span>
                      {isMultiply ? '★ ' : ''}
                      {p.name ? <strong>{p.name}</strong> : p.email}
                      {p.name && <span style={{ color: '#64748b', marginLeft: 6 }}>{p.email}</span>}
                      {p.isHost && <span style={{ color: '#94a3b8', marginLeft: 6, fontSize: 11 }}>host</span>}
                    </span>
                    {isMultiply && (
                      <span style={{ fontSize: 11, color: '#0784a7' }}>
                        {multiplyUser.userType}
                        {companyNames.length > 0 ? ` · ${companyNames.join(', ')}` : ' · no company assigned'}
                      </span>
                    )}
                  </div>
                </ParticipantChip>
              );
            })}
          </ParticipantList>
        </PanelSection>
      )}

      {/* Assign form, review form, or processed summary */}
      {isProcessed ? (
        <PanelSection>
          <PanelSectionTitle>Assignment Summary</PanelSectionTitle>
          <PanelGrid $cols={3}>
            <PanelField>
              <PanelLabel>Status</PanelLabel>
              <PanelValue><StatusBadge $variant="done">{triageData.status}</StatusBadge></PanelValue>
            </PanelField>
            <PanelField>
              <PanelLabel>Assigned Company</PanelLabel>
              <PanelValue>{triageData.assignedCompanyId || '—'}</PanelValue>
            </PanelField>
            <PanelField>
              <PanelLabel>Assigned To</PanelLabel>
              <PanelValue>{triageData.assignedTo || '—'}</PanelValue>
            </PanelField>
            <PanelField>
              <PanelLabel>Processed By</PanelLabel>
              <PanelValue>{triageData.processedBy || '—'}</PanelValue>
            </PanelField>
            <PanelField>
              <PanelLabel>Processed At</PanelLabel>
              <PanelValue>{formatDateTime(triageData.processedAt)}</PanelValue>
            </PanelField>
            {triageData.notes && (
              <PanelField>
                <PanelLabel>Notes</PanelLabel>
                <PanelValue>{triageData.notes}</PanelValue>
              </PanelField>
            )}
          </PanelGrid>
        </PanelSection>
      ) : mode === 'review' ? (
        /* Mark as Reviewed form */
        <PanelSection>
          <PanelSectionTitle>Mark as Reviewed</PanelSectionTitle>
          <FormRow>
            <FormField>
              <FormLabel>Notes (optional)</FormLabel>
              <FormTextarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Any notes about your review decision…"
              />
            </FormField>
          </FormRow>
          {error && <FormErrorText>{error}</FormErrorText>}
          <PanelActions>
            <PrimaryButton onClick={handleReview} disabled={submitting}>
              {submitting ? 'Saving…' : 'Mark as Reviewed'}
            </PrimaryButton>
          </PanelActions>
        </PanelSection>
      ) : (
        /* Assignment form — shown by default for any unprocessed triage item */
        <PanelSection>
          <PanelSectionTitle>Assign Meeting</PanelSectionTitle>
          <FormRow $cols={2}>
            <FormField>
              <FormLabel>Company *</FormLabel>
              <FormSelect value={companyId} onChange={e => setCompanyId(e.target.value)}>
                <option value="">— Select company —</option>
                {companies.map(c => (
                  <option key={c.company.id} value={c.company.id}>{c.company.name}</option>
                ))}
              </FormSelect>
            </FormField>
            <FormField>
              <FormLabel>Assign to user * {loadingUsers ? '(loading…)' : ''}</FormLabel>
              <FormSelect value={userId} onChange={e => setUserId(e.target.value)} disabled={!companyId || loadingUsers}>
                <option value="">— Select user —</option>
                {users.map(u => (
                  <option key={u.userId} value={u.userId}>{u.firstName} {u.lastName} ({u.email})</option>
                ))}
              </FormSelect>
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FormLabel>Notes (optional)</FormLabel>
              <FormTextarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Any notes about this assignment…"
              />
            </FormField>
          </FormRow>
          {error && <FormErrorText>{error}</FormErrorText>}
          <PanelActions>
            <PrimaryButton onClick={handleAssign} disabled={submitting || !companyId || !userId}>
              {submitting ? 'Assigning…' : 'Assign'}
            </PrimaryButton>
          </PanelActions>
        </PanelSection>
      )}
    </ExpandedPanel>
  );
}
